import React, { useEffect, useState } from 'react';
import api from '../services/api';
import GardenModal from './GardenModal';
import AdminUserDetail from './AdminUserDetail';
import './AdminUserList.css';

const MS_7_DAYS  = 7  * 86400000;
const MS_30_DAYS = 30 * 86400000;

const SORT_OPTIONS = [
  { value: 'signup',     label: 'Signup date (newest first)' },
  { value: 'login',      label: 'Last login' },
  { value: 'plants',     label: 'Plant count' },
  { value: 'properties', label: 'Property count' },
  { value: 'zone',       label: 'USDA zone' },
  { value: 'tier',       label: 'Subscription tier' },
  { value: 'state',      label: 'State' },
];

function applyActiveFilter(users, activeFilter) {
  if (!activeFilter || activeFilter.type === 'all') return users;
  const now = Date.now();
  switch (activeFilter.type) {
    case 'tier':
      return users.filter(u => (u.subscription_tier || 'free').toLowerCase() === activeFilter.value);
    case 'zone':
      return users.filter(u => (u.usda_zone || '') === activeFilter.value);
    case 'active7':
      return users.filter(u => u.last_login_at && (now - new Date(u.last_login_at).getTime()) <= MS_7_DAYS);
    case 'active30':
      return users.filter(u => u.last_login_at && (now - new Date(u.last_login_at).getTime()) <= MS_30_DAYS);
    case 'hasPlants':
      return users.filter(u => Number(u.plant_count) > 0);
    case 'hasPhotos':
      return users.filter(u => Number(u.photo_count) > 0);
    case 'hasTasks':
      return users.filter(u => Number(u.task_count) > 0);
    case 'signupDate':
      return users.filter(u => u.created_at && u.created_at.startsWith(activeFilter.value));
    case 'userId':
      return users.filter(u => String(u.id) === String(activeFilter.value));
    default:
      return users;
  }
}

function formatDate(ts) {
  if (!ts) return 'Never';
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function applySort(users, sortBy) {
  const arr = [...users];
  switch (sortBy) {
    case 'login':
      return arr.sort((a, b) => {
        if (!a.last_login_at && !b.last_login_at) return 0;
        if (!a.last_login_at) return 1;
        if (!b.last_login_at) return -1;
        return new Date(b.last_login_at) - new Date(a.last_login_at);
      });
    case 'plants':
      return arr.sort((a, b) => Number(b.plant_count) - Number(a.plant_count));
    case 'properties':
      return arr.sort((a, b) => Number(b.property_count) - Number(a.property_count));
    case 'zone':
      return arr.sort((a, b) => (a.usda_zone || 'zzz').localeCompare(b.usda_zone || 'zzz'));
    case 'tier':
      return arr.sort((a, b) => (a.subscription_tier || '').localeCompare(b.subscription_tier || ''));
    case 'state':
      return arr.sort((a, b) => (a.state || 'zzz').localeCompare(b.state || 'zzz'));
    case 'signup':
    default:
      return arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }
}

function AdminUserList({ activeFilter, setActiveFilter }) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('signup');
  const [expandedId, setExpandedId] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [resetStatus, setResetStatus] = useState({});
  const [modal, setModal] = useState({ isOpen: false });
  const [selectedUserId, setSelectedUserId] = useState(null);

  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/users/list');
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('AdminUserList: fetchUsers failed:', err);
      setError(err.response?.status === 403 ? 'Access denied' : 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!expandedId || userDetails[expandedId]) return;
    setUserDetails(prev => ({ ...prev, [expandedId]: { properties: [], loading: true, error: null } }));
    api.get(`/admin/users/${expandedId}`)
      .then(res => {
        setUserDetails(prev => ({
          ...prev,
          [expandedId]: { properties: res.data.properties || [], loading: false, error: null },
        }));
      })
      .catch(() => {
        setUserDetails(prev => ({
          ...prev,
          [expandedId]: { properties: [], loading: false, error: 'Failed to load properties' },
        }));
      });
  }, [expandedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredAndSorted = applySort(
    applyActiveFilter(users, activeFilter).filter(u => {
      const q = searchQuery.toLowerCase();
      return (
        (u.email              || '').toLowerCase().includes(q) ||
        (u.usda_zone          || '').toLowerCase().includes(q) ||
        (u.state              || '').toLowerCase().includes(q) ||
        (u.subscription_tier  || '').toLowerCase().includes(q)
      );
    }),
    sortBy
  );

  const handleToggleRow = (id) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const handleSendReset = async (user) => {
    setResetStatus(prev => ({ ...prev, [user.id]: { loading: true, message: null, isError: false } }));
    try {
      await api.post(`/admin/users/${user.id}/send-password-reset`);
      setResetStatus(prev => ({
        ...prev,
        [user.id]: { loading: false, message: 'Reset email sent', isError: false },
      }));
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send reset email';
      setResetStatus(prev => ({
        ...prev,
        [user.id]: { loading: false, message: msg, isError: true },
      }));
    }
  };

  const handleDeleteUser = (user) => {
    setModal({
      isOpen: true,
      type: 'confirm',
      title: 'Delete User',
      message: `Permanently delete ${user.email} and all their data? This cannot be undone.`,
      onCancel: () => setModal({ isOpen: false }),
      onConfirm: async () => {
        setModal({ isOpen: false });
        try {
          await api.delete(`/admin/users/${user.id}`);
          setUsers(prev => prev.filter(u => u.id !== user.id));
          if (expandedId === user.id) setExpandedId(null);
        } catch (err) {
          setModal({
            isOpen: true,
            type: 'alert',
            title: 'Error',
            message: err.response?.data?.error || 'Failed to delete user. Please try again.',
            onConfirm: () => setModal({ isOpen: false }),
          });
        }
      },
    });
  };

  if (isLoading) return <div className="aul-loading">Loading users...</div>;
  if (error) return <div className="aul-error">{error}</div>;

  return (
    <div className="aul-container">
      <GardenModal
        isOpen={modal.isOpen}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
      />
      <AdminUserDetail
        key={selectedUserId}
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
      />

      {activeFilter && (
        <div className="aul-filter-chip">
          <span className="aul-filter-chip-label">{activeFilter.label}</span>
          <button className="aul-filter-chip-clear" onClick={() => setActiveFilter(null)}>✕</button>
        </div>
      )}

      <div className="aul-controls">
        <input
          className="aul-search"
          type="text"
          placeholder="Search by email, zone, state, or tier..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <select
          className="aul-sort"
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
        >
          {SORT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {filteredAndSorted.length === 0 ? (
        <div className="aul-empty">No users found</div>
      ) : (
        <div className="aul-list">
          {filteredAndSorted.map(user => {
            const isExpanded = expandedId === user.id;
            const detail = userDetails[user.id];
            const rs = resetStatus[user.id];
            const tier = (user.subscription_tier || 'free').toLowerCase();

            return (
              <div key={user.id} className={`aul-row${isExpanded ? ' aul-row--expanded' : ''}`}>
                <div className="aul-summary" onClick={() => handleToggleRow(user.id)}>
                  <div className="aul-summary-left">
                    <span className="aul-email">{user.email}</span>
                    <span className="aul-meta">
                      Joined {formatDate(user.created_at)} · Last login: {formatDate(user.last_login_at)}
                    </span>
                    <span className="aul-meta">
                      {user.plant_count} plants · {user.photo_count} photos · {user.task_count} tasks
                    </span>
                  </div>
                  <div className="aul-summary-right">
                    <span className="aul-meta">Zone {user.usda_zone || '—'} · {user.state || '—'}</span>
                    <span className={`aul-tier-badge aul-tier-badge--${tier}`}>{tier}</span>
                    <span className={`aul-verified aul-verified--${user.email_verified ? 'yes' : 'no'}`}>
                      {user.email_verified ? '✓' : '✗'}
                    </span>
                    <span className="aul-chevron">{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </div>

                <div className={`aul-expanded-panel${isExpanded ? ' aul-expanded-panel--open' : ''}`}>
                  <div className="aul-expanded-inner">
                    <div className="aul-counts">
                      {user.property_count} {Number(user.property_count) === 1 ? 'property' : 'properties'} · {user.plant_count} plants · {user.photo_count} photos · {user.task_count} tasks
                    </div>

                    <div className="aul-properties-section">
                      <div className="aul-properties-title">Properties</div>
                      {detail?.loading && (
                        <div className="aul-properties-loading">Loading...</div>
                      )}
                      {detail?.error && (
                        <div className="aul-properties-error">{detail.error}</div>
                      )}
                      {detail && !detail.loading && !detail.error && (
                        detail.properties.length === 0 ? (
                          <div className="aul-properties-empty">No properties yet</div>
                        ) : (
                          <div className="aul-properties-list">
                            {detail.properties.map(p => (
                              <div key={p.id} className="aul-property-row">
                                <span className="aul-property-name">{p.name}</span>
                                <span className="aul-property-meta">
                                  {p.address}{p.usda_zone ? ` · Zone ${p.usda_zone}` : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        )
                      )}
                    </div>

                    <div className="aul-actions">
                      <button
                        className="aul-action-btn aul-action-btn--secondary"
                        onClick={() => setSelectedUserId(user.id)}
                      >
                        View Full Profile
                      </button>
                      <button
                        className="aul-action-btn aul-action-btn--secondary"
                        disabled={rs?.loading}
                        onClick={() => handleSendReset(user)}
                      >
                        {rs?.loading ? 'Sending...' : 'Send Password Reset Email'}
                      </button>
                      <button
                        className="aul-action-btn aul-action-btn--danger"
                        onClick={() => handleDeleteUser(user)}
                      >
                        Delete User
                      </button>
                    </div>

                    {rs?.message && (
                      <div className={`aul-reset-status aul-reset-status--${rs.isError ? 'error' : 'success'}`}>
                        {rs.message}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdminUserList;
